import React, { useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { MapPin, FileText, CheckCircle2, Download } from 'lucide-react';
import { PricingCard } from '../components/PricingCard';
import jsPDF from 'jspdf';
export function CoachingPage() {
  const [waiverSigned, setWaiverSigned] = useState(false);
  const [mediaConsent, setMediaConsent] = useState<'agree' | 'disagree' | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientDate, setClientDate] = useState('');
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let y = 20;
    const addText = (text: string, size: number, style: string = 'normal', align: string = 'left') => {
      doc.setFontSize(size);
      doc.setFont('helvetica', style);
      const lines = doc.splitTextToSize(text, maxWidth);
      if (y + lines.length * (size * 0.5) > 270) {
        doc.addPage();
        y = 20;
      }
      if (align === 'center') {
        lines.forEach((line: string) => {
          const lineWidth = doc.getTextWidth(line);
          doc.text(line, (pageWidth - lineWidth) / 2, y);
          y += size * 0.5;
        });
      } else {
        doc.text(lines, margin, y);
        y += lines.length * (size * 0.5);
      }
    };
    const addSpace = (px: number) => {
      y += px;
    };
    const checkNewPage = (needed: number) => {
      if (y + needed > 270) {
        doc.addPage();
        y = 20;
      }
    };
    // Title
    addText('TRAINING AGREEMENT, RELEASE OF LIABILITY', 14, 'bold', 'center');
    addText('& ASSUMPTION OF RISK', 14, 'bold', 'center');
    addSpace(8);
    // Client info
    addText(`Client Name: ${clientName}`, 11, 'normal');
    addText(`Date: ${clientDate}`, 11, 'normal');
    addSpace(8);
    // Section I
    addText('I. RELEASE OF LIABILITY & ASSUMPTION OF RISK', 12, 'bold');
    addSpace(4);
    addText('Nature of Activity', 10, 'bold');
    addText('I, the undersigned, acknowledge that kickboxing, boxing, and physical conditioning are high-intensity activities involving physical exertion, striking movements, and contact with equipment and/or training partners.', 9, 'normal');
    addSpace(4);
    addText('Assumption of Risk', 10, 'bold');
    addText('I voluntarily assume all risks of injury, whether known or unknown, that may arise from training or the use of gym equipment (including but not limited to: heavy bags, pads, mats, wall-mounted bars, rollers, and other training tools). These risks include, but are not limited to:', 9, 'normal');
    addSpace(2);
    const risks = ['• Muscle strains, ligament tears, or joint injuries', '• Cuts, scrapes, swelling, or bruising', '• Physical exhaustion, dehydration, or dizziness', '• Contact-related injuries', '• Any other physical or medical injury resulting from intense exercise or equipment use'];
    risks.forEach(r => addText(r, 9, 'normal'));
    addSpace(4);
    addText('Release of Liability', 10, 'bold');
    addText('To the fullest extent permitted by applicable law, I hereby release, waive, and discharge Aleks Szulc ("the Coach") from any and all liability, claims, or demands for personal injury or property damage arising out of my participation in training sessions. This release does not apply to damages resulting from gross negligence or intentional misconduct.', 9, 'normal');
    addSpace(4);
    addText('Indemnification', 10, 'bold');
    addText('I agree to hold the Coach harmless from any loss, liability, or costs (including legal fees) incurred as a result of any claim or lawsuit brought by me or on my behalf related to my participation.', 9, 'normal');
    addSpace(4);
    addText('Medical Fitness & Responsibility', 10, 'bold');
    addText('I confirm that I am in good physical health and capable of participating in intense physical activity. I have disclosed any relevant medical conditions. I understand that if I have any doubts, I should consult a physician before training. I agree to immediately stop training and inform the Coach if I feel pain, discomfort, dizziness, or any unusual symptoms.', 9, 'normal');
    addSpace(4);
    addText('Emergency Medical Consent', 10, 'bold');
    addText('In the event of injury or medical emergency, I authorize the Coach to seek medical assistance on my behalf, including contacting emergency services. I acknowledge that I am responsible for any medical costs incurred.', 9, 'normal');
    addSpace(8);
    // Section II
    checkNewPage(60);
    addText('II. TRAINING CONDITIONS & CONDUCT', 12, 'bold');
    addSpace(4);
    addText('Compliance with Instructions', 10, 'bold');
    addText('I agree to follow all safety instructions and guidance provided by the Coach. I understand that failure to do so increases my risk of injury and may result in termination of the session.', 9, 'normal');
    addSpace(4);
    addText('Training Environment', 10, 'bold');
    addText('I understand that sessions may take place in gyms, outdoor areas, or other training environments, and I accept all risks associated with these locations.', 9, 'normal');
    addSpace(4);
    addText('Conduct', 10, 'bold');
    addText('The Coach reserves the right to terminate a session or package (without refund) if I display unsafe, aggressive, or inappropriate behavior, or fail to follow instructions.', 9, 'normal');
    addSpace(8);
    // Section III
    checkNewPage(80);
    addText('III. PAYMENT & PACKAGE POLICY', 12, 'bold');
    addSpace(4);
    addText('No-Refund Policy', 10, 'bold');
    addText('All payments for individual sessions and training packages are final and non-refundable. By purchasing a package, the Client acknowledges that they are committing to the full value of the services.', 9, 'normal');
    addSpace(2);
    addText('Refunds will not be issued except where required by applicable law or in limited exceptional circumstances, such as serious injury preventing participation (supported by valid medical documentation). In such cases, any refund, partial refund, or credit remains solely at the reasonable discretion of the Coach, evaluated on a case-by-case basis.', 9, 'normal');
    addSpace(2);
    addText('The Coach reserves the right, but is not obligated, to offer a refund, partial refund, or alternative solution in specific situations where it is deemed appropriate.', 9, 'normal');
    addSpace(4);
    addText('No Expiration', 10, 'bold');
    addText('Purchased training sessions do not expire and remain valid until used.', 9, 'normal');
    addSpace(4);
    addText('Non-Transferable', 10, 'bold');
    addText('All sessions are assigned to the registered Client only and cannot be transferred, gifted, or sold.', 9, 'normal');
    addSpace(8);
    // Section IV
    checkNewPage(40);
    addText('IV. CANCELLATION POLICY', 12, 'bold');
    addSpace(4);
    addText('24-Hour Notice Requirement', 10, 'bold');
    addText('Cancellations or rescheduling must be made at least 24 hours in advance. Failure to provide sufficient notice will result in the session being forfeited and deducted from the package balance.', 9, 'normal');
    addSpace(8);
    // Section V
    checkNewPage(50);
    addText('V. DATA PROTECTION (GDPR)', 12, 'bold');
    addSpace(4);
    addText('I acknowledge that Aleks Szulc processes my personal data (such as name and contact details) solely for the purpose of providing training services, scheduling sessions, and communication.', 9, 'normal');
    addSpace(2);
    addText('I understand that:', 9, 'normal');
    addText('• My data will not be shared with third parties without my consent, unless required by law', 9, 'normal');
    addText('• I have the right to access, correct, or request deletion of my data', 9, 'normal');
    addText('• I may withdraw consent at any time by contacting the Coach', 9, 'normal');
    addSpace(8);
    // Section VI
    checkNewPage(40);
    addText('VI. MEDIA RELEASE (OPTIONAL)', 12, 'bold');
    addSpace(4);
    addText('I grant permission for the Coach to take photos and videos during training sessions and use them for promotional purposes (e.g., social media, website), unless I explicitly withdraw this consent in writing.', 9, 'normal');
    addSpace(4);
    addText(`[${mediaConsent === 'agree' ? 'X' : ' '}] I agree          [${mediaConsent === 'disagree' ? 'X' : ' '}] I do NOT agree`, 10, 'bold');
    addSpace(8);
    // Section VII
    checkNewPage(30);
    addText('VII. GOVERNING LAW', 12, 'bold');
    addSpace(4);
    addText('This Agreement shall be governed by and interpreted in accordance with the laws of Poland.', 9, 'normal');
    addSpace(12);
    // Acknowledgment
    checkNewPage(60);
    addText('ACKNOWLEDGMENT', 12, 'bold', 'center');
    addSpace(4);
    addText('I HAVE READ THIS AGREEMENT, FULLY UNDERSTAND ITS TERMS, AND VOLUNTARILY SIGN IT.', 10, 'bold', 'center');
    addSpace(16);
    // Signatures
    addText('Participant Signature:', 10, 'bold');
    addSpace(2);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(14);
    doc.text(clientName, margin, y);
    y += 4;
    doc.setDrawColor(0);
    doc.line(margin, y, margin + 80, y);
    addSpace(4);
    addText('(Parent/Guardian if under 18)', 8, 'italic');
    addSpace(12);
    addText('Coach Signature:', 10, 'bold');
    addSpace(2);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(14);
    doc.text('Aleks Szulc', margin, y);
    y += 4;
    doc.setDrawColor(0);
    doc.line(margin, y, margin + 80, y);
    doc.save(`Training_Agreement_${clientName.replace(/\s+/g, '_')}.pdf`);
  };
  const waiverRef = useRef<HTMLDivElement>(null);
  const handleWaiverSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mediaConsent === null) {
      alert('Please select your media release preference.');
      return;
    }
    setWaiverSigned(true);
    setTimeout(() => {
      waiverRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };
  return <main className="min-h-screen pt-32 pb-20 px-6" data-id="element-89">
      <div className="max-w-7xl mx-auto" data-id="element-90">
        {/* Hero Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-10" data-id="element-91">
          <h1 className="text-5xl md:text-7xl font-heading text-white mb-4" data-id="element-92">
            TRAIN WITH ALEKS
          </h1>
          <p className="max-w-2xl mx-auto text-gray-400 font-body" data-id="element-93">
            Train with a National Champion. Whether you're stepping on to the
            mat for the first time or preparing for competition, get elite-level
            coaching tailored to your goals.
          </p>
        </motion.div>

        {/* Pricing Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex lg:grid lg:grid-cols-5 gap-6 lg:gap-6 items-center mb-4 lg:mb-24 overflow-x-auto snap-x snap-mandatory pt-6 pb-4 lg:pt-0 lg:pb-0 lg:overflow-visible scrollbar-hide" data-id="element-94">
          <motion.div variants={itemVariants} className="h-full min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-95">
            <PricingCard title="THE ENTRY" price="50 PLN" description="Your first step on to the mat. Trial session." features={[{
            name: 'Technique assessment',
            included: true
          }, {
            name: 'Personalized feedback',
            included: true
          }, {
            name: 'Custom training plan',
            included: false
          }, {
            name: 'Priority scheduling',
            included: false
          }, {
            name: 'Nutrition guidance',
            included: false
          }, {
            name: 'Progress tracking',
            included: false
          }, {
            name: 'Personalized fight strategy',
            included: false
          }, {
            name: 'Monthly performance review',
            included: false
          }]} ctaText="BOOK TRIAL" ctaLink="/contact" data-id="element-96" />
          </motion.div>

          <motion.div variants={itemVariants} className="h-full min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-97">
            <PricingCard title="SINGLE SESSION" price="150 PLN" description="Train on your schedule. Pay as you go." features={[{
            name: 'Technique assessment',
            included: true
          }, {
            name: 'Personalized feedback',
            included: true
          }, {
            name: 'Custom training plan',
            included: true
          }, {
            name: 'Priority scheduling',
            included: false
          }, {
            name: 'Nutrition guidance',
            included: false
          }, {
            name: 'Progress tracking',
            included: false
          }, {
            name: 'Personalized fight strategy',
            included: false
          }, {
            name: 'Monthly performance review',
            included: false
          }]} ctaText="BOOK SESSION" ctaLink="/contact" data-id="element-98" />
          </motion.div>

          <motion.div variants={itemVariants} className="h-full min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-99">
            <PricingCard title="THE STARTER" price="550 PLN" description="Pack of 4 sessions (137 PLN/session)." features={[{
            name: 'Technique assessment',
            included: true
          }, {
            name: 'Personalized feedback',
            included: true
          }, {
            name: 'Custom training plan',
            included: true
          }, {
            name: 'Priority scheduling',
            included: true
          }, {
            name: 'Nutrition guidance',
            included: false
          }, {
            name: 'Progress tracking',
            included: false
          }, {
            name: 'Personalized fight strategy',
            included: false
          }, {
            name: 'Monthly performance review',
            included: false
          }]} ctaText="GET 4-PACK" ctaLink="/contact" data-id="element-100" />
          </motion.div>

          <motion.div variants={itemVariants} className="h-full lg:scale-105 z-10 min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-101">
            <PricingCard title="THE COMMITMENT" price="1050 PLN" description="Pack of 8 sessions (131 PLN/session)." features={[{
            name: 'Technique assessment',
            included: true
          }, {
            name: 'Personalized feedback',
            included: true
          }, {
            name: 'Custom training plan',
            included: true
          }, {
            name: 'Priority scheduling',
            included: true
          }, {
            name: 'Nutrition guidance',
            included: true
          }, {
            name: 'Progress tracking',
            included: true
          }, {
            name: 'Personalized fight strategy',
            included: false
          }, {
            name: 'Monthly performance review',
            included: false
          }]} isPopular={true} isLarge={true} badgeText="MOST POPULAR" ctaText="GET 8-PACK" ctaLink="/contact" data-id="element-102" />
          </motion.div>

          <motion.div variants={itemVariants} className="h-full lg:scale-105 z-10 min-w-[280px] snap-center shrink-0 lg:min-w-0 lg:shrink" data-id="element-103">
            <PricingCard title="THE PRO" price="1450 PLN" description="Pack of 12 sessions (121 PLN/session)." features={[{
            name: 'Technique assessment',
            included: true
          }, {
            name: 'Personalized feedback',
            included: true
          }, {
            name: 'Custom training plan',
            included: true
          }, {
            name: 'Priority scheduling',
            included: true
          }, {
            name: 'Nutrition guidance',
            included: true
          }, {
            name: 'Progress tracking',
            included: true
          }, {
            name: 'Personalized fight strategy',
            included: true
          }, {
            name: 'Monthly performance review',
            included: true
          }]} isPopular={true} isLarge={true} badgeText="BEST VALUE" ctaText="GET 12-PACK" ctaLink="/contact" data-id="element-104" />
          </motion.div>
        </motion.div>

        {/* Swipe Indicator - mobile only */}
        <div className="flex lg:hidden items-center justify-center gap-2 mb-16" data-id="element-105">
          <div className="flex items-center gap-1.5 text-gray-500 text-sm font-body" data-id="element-106">
            <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-107">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" data-id="element-108" />
            </svg>
            <span data-id="element-109">Swipe to see all plans</span>
            <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-110">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-111" />
            </svg>
          </div>
        </div>

        {/* Small Group Sessions */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-24" data-id="element-112">
          <h2 className="text-3xl font-heading text-white text-center mb-10" data-id="element-113">
            SMALL GROUP SESSIONS
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-114">
            {[{
            people: '2 People',
            price: '200 PLN'
          }, {
            people: '3 People',
            price: '300 PLN'
          }, {
            people: '4 People',
            price: '375 PLN'
          }].map((group, idx) => <div key={idx} className="bg-dark-elevated border border-dark-border rounded-xl p-6 text-center hover:border-accent-blue transition-colors" data-id="element-115">
                <h3 className="text-xl font-heading text-gray-300 mb-2" data-id="element-116">
                  {group.people}
                </h3>
                <p className="text-3xl font-heading text-white mb-6" data-id="element-117">
                  {group.price}
                </p>
                <a href="/#/contact" className="inline-block w-full py-2 border border-accent-blue text-accent-blue rounded-lg font-heading tracking-wider hover:bg-accent-blue hover:text-black transition-colors" data-id="element-118">
                  BOOK GROUP
                </a>
              </div>)}
          </div>
        </motion.div>

        {/* Contract & Waiver Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-24" ref={waiverRef} data-id="element-119">
          <div className="max-w-3xl mx-auto" data-id="element-120">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-121">
              <FileText className="w-7 h-7 text-accent-blue" data-id="element-122" />
              <h2 className="text-3xl font-heading text-white text-center" data-id="element-123">
                CONTRACT & WAIVER
              </h2>
            </div>
            <p className="text-gray-400 font-body text-center mb-10" data-id="element-124">
              Please read and sign the agreement below before your first
              session.
            </p>

            <div className="bg-dark-card border border-dark-border rounded-2xl p-8" data-id="element-125">
              {waiverSigned ? <div className="flex flex-col items-center justify-center py-12 text-center space-y-4" data-id="element-126">
                  <CheckCircle2 className="w-16 h-16 text-accent-blue" data-id="element-127" />
                  <h3 className="text-3xl font-heading text-white" data-id="element-128">
                    AGREEMENT SIGNED
                  </h3>
                  <p className="text-gray-400 font-body" data-id="element-129">
                    Thank you for signing. Please download your contract below
                    and send it to Aleks to complete your registration.
                  </p>
                  <button onClick={generatePDF} className="flex items-center gap-3 bg-accent-blue text-black font-heading tracking-wider text-lg px-8 py-4 rounded-lg hover:bg-accent-blue-hover transition-colors mt-4" data-id="element-130">
                    <Download className="w-5 h-5" data-id="element-131" />
                    DOWNLOAD SIGNED CONTRACT
                  </button>
                  <a href="/#/contact" className="flex items-center justify-center gap-3 border border-accent-blue text-accent-blue font-heading tracking-wider text-lg px-8 py-4 rounded-lg hover:bg-accent-blue hover:text-black transition-colors" data-id="element-132">
                    CONTACT ALEKS
                  </a>
                </div> : <>
                  {/* Contract Text */}
                  <div className="bg-dark-primary border border-dark-border rounded-xl p-6 mb-8 max-h-96 overflow-y-auto" data-id="element-133">
                    <h3 className="text-xl font-heading text-white mb-6 text-center" data-id="element-134">
                      TRAINING AGREEMENT, RELEASE OF LIABILITY & ASSUMPTION OF
                      RISK
                    </h3>

                    <div className="text-sm text-gray-400 font-body space-y-6" data-id="element-135">
                      {/* Section I */}
                      <div data-id="element-136">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-137">
                          I. RELEASE OF LIABILITY & ASSUMPTION OF RISK
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-138">
                          Nature of Activity
                        </p>
                        <p className="mb-3" data-id="element-139">
                          I, the undersigned, acknowledge that kickboxing,
                          boxing, and physical conditioning are high-intensity
                          activities involving physical exertion, striking
                          movements, and contact with equipment and/or training
                          partners.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-140">
                          Assumption of Risk
                        </p>
                        <p className="mb-2" data-id="element-141">
                          I voluntarily assume all risks of injury, whether
                          known or unknown, that may arise from training or the
                          use of gym equipment (including but not limited to:
                          heavy bags, pads, mats, wall-mounted bars, rollers,
                          and other training tools). These risks include, but
                          are not limited to:
                        </p>
                        <ul className="list-disc list-inside space-y-1 mb-3 ml-2" data-id="element-142">
                          <li data-id="element-143">
                            Muscle strains, ligament tears, or joint injuries
                          </li>
                          <li data-id="element-144">Cuts, scrapes, swelling, or bruising</li>
                          <li data-id="element-145">
                            Physical exhaustion, dehydration, or dizziness
                          </li>
                          <li data-id="element-146">Contact-related injuries</li>
                          <li data-id="element-147">
                            Any other physical or medical injury resulting from
                            intense exercise or equipment use
                          </li>
                        </ul>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-148">
                          Release of Liability
                        </p>
                        <p className="mb-3" data-id="element-149">
                          To the fullest extent permitted by applicable law, I
                          hereby release, waive, and discharge Aleks Szulc ("the
                          Coach") from any and all liability, claims, or demands
                          for personal injury or property damage arising out of
                          my participation in training sessions. This release
                          does not apply to damages resulting from gross
                          negligence or intentional misconduct.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-150">
                          Indemnification
                        </p>
                        <p className="mb-3" data-id="element-151">
                          I agree to hold the Coach harmless from any loss,
                          liability, or costs (including legal fees) incurred as
                          a result of any claim or lawsuit brought by me or on
                          my behalf related to my participation.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-152">
                          Medical Fitness & Responsibility
                        </p>
                        <p className="mb-3" data-id="element-153">
                          I confirm that I am in good physical health and
                          capable of participating in intense physical activity.
                          I have disclosed any relevant medical conditions. I
                          understand that if I have any doubts, I should consult
                          a physician before training. I agree to immediately
                          stop training and inform the Coach if I feel pain,
                          discomfort, dizziness, or any unusual symptoms.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-154">
                          Emergency Medical Consent
                        </p>
                        <p data-id="element-155">
                          In the event of injury or medical emergency, I
                          authorize the Coach to seek medical assistance on my
                          behalf, including contacting emergency services. I
                          acknowledge that I am responsible for any medical
                          costs incurred.
                        </p>
                      </div>

                      {/* Section II */}
                      <div data-id="element-156">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-157">
                          II. TRAINING CONDITIONS & CONDUCT
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-158">
                          Compliance with Instructions
                        </p>
                        <p className="mb-3" data-id="element-159">
                          I agree to follow all safety instructions and guidance
                          provided by the Coach. I understand that failure to do
                          so increases my risk of injury and may result in
                          termination of the session.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-160">
                          Training Environment
                        </p>
                        <p className="mb-3" data-id="element-161">
                          I understand that sessions may take place in gyms,
                          outdoor areas, or other training environments, and I
                          accept all risks associated with these locations.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-162">
                          Conduct
                        </p>
                        <p data-id="element-163">
                          The Coach reserves the right to terminate a session or
                          package (without refund) if I display unsafe,
                          aggressive, or inappropriate behavior, or fail to
                          follow instructions.
                        </p>
                      </div>

                      {/* Section III */}
                      <div data-id="element-164">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-165">
                          III. PAYMENT & PACKAGE POLICY
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-166">
                          No-Refund Policy
                        </p>
                        <p className="mb-3" data-id="element-167">
                          All payments for individual sessions and training
                          packages are final and non-refundable. By purchasing a
                          package, the Client acknowledges that they are
                          committing to the full value of the services.
                        </p>
                        <p className="mb-3" data-id="element-168">
                          Refunds will not be issued except where required by
                          applicable law or in limited exceptional
                          circumstances, such as serious injury preventing
                          participation (supported by valid medical
                          documentation). In such cases, any refund, partial
                          refund, or credit remains solely at the reasonable
                          discretion of the Coach, evaluated on a case-by-case
                          basis.
                        </p>
                        <p className="mb-3" data-id="element-169">
                          The Coach reserves the right, but is not obligated, to
                          offer a refund, partial refund, or alternative
                          solution in specific situations where it is deemed
                          appropriate.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-170">
                          No Expiration
                        </p>
                        <p className="mb-3" data-id="element-171">
                          Purchased training sessions do not expire and remain
                          valid until used.
                        </p>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-172">
                          Non-Transferable
                        </p>
                        <p data-id="element-173">
                          All sessions are assigned to the registered Client
                          only and cannot be transferred, gifted, or sold.
                        </p>
                      </div>

                      {/* Section IV */}
                      <div data-id="element-174">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-175">
                          IV. CANCELLATION POLICY
                        </h4>

                        <p className="text-gray-300 font-semibold mb-1" data-id="element-176">
                          24-Hour Notice Requirement
                        </p>
                        <p data-id="element-177">
                          Cancellations or rescheduling must be made at least 24
                          hours in advance. Failure to provide sufficient notice
                          will result in the session being forfeited and
                          deducted from the package balance.
                        </p>
                      </div>

                      {/* Section V */}
                      <div data-id="element-178">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-179">
                          V. DATA PROTECTION (GDPR)
                        </h4>

                        <p className="mb-2" data-id="element-180">
                          I acknowledge that Aleks Szulc processes my personal
                          data (such as name and contact details) solely for the
                          purpose of providing training services, scheduling
                          sessions, and communication.
                        </p>
                        <p className="mb-2" data-id="element-181">I understand that:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-182">
                          <li data-id="element-183">
                            My data will not be shared with third parties
                            without my consent, unless required by law
                          </li>
                          <li data-id="element-184">
                            I have the right to access, correct, or request
                            deletion of my data
                          </li>
                          <li data-id="element-185">
                            I may withdraw consent at any time by contacting the
                            Coach
                          </li>
                        </ul>
                      </div>

                      {/* Section VII */}
                      <div data-id="element-186">
                        <h4 className="text-base font-heading text-accent-blue mb-3" data-id="element-187">
                          VII. GOVERNING LAW
                        </h4>
                        <p data-id="element-188">
                          This Agreement shall be governed by and interpreted in
                          accordance with the laws of Poland.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Waiver Form */}
                  <form onSubmit={handleWaiverSubmit} className="space-y-6" data-id="element-189">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-190">
                      <div data-id="element-191">
                        <label htmlFor="waiver-name" className="block text-sm font-body text-gray-400 mb-2" data-id="element-192">
                          Client Name
                        </label>
                        <input type="text" id="waiver-name" required value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Enter your full name" className="w-full bg-dark-primary border border-dark-border rounded-lg px-4 py-3 text-white font-body focus:outline-none focus:border-accent-blue transition-colors" data-id="element-193" />
                      </div>
                      <div data-id="element-194">
                        <label htmlFor="waiver-date" className="block text-sm font-body text-gray-400 mb-2" data-id="element-195">
                          Date
                        </label>
                        <input type="text" id="waiver-date" required placeholder="DD/MM/YYYY" value={clientDate} onChange={e => {
                      let val = e.target.value.replace(/[^\d]/g, '');
                      if (val.length > 8) val = val.slice(0, 8);
                      if (val.length >= 5) {
                        val = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4);
                      } else if (val.length >= 3) {
                        val = val.slice(0, 2) + '/' + val.slice(2);
                      }
                      setClientDate(val);
                    }} maxLength={10} className="w-full bg-dark-primary border border-dark-border rounded-lg px-4 py-3 text-white font-body focus:outline-none focus:border-accent-blue transition-colors" data-id="element-196" />
                      </div>
                    </div>

                    {/* VI. Media Release */}
                    <div className="bg-dark-elevated border border-dark-border rounded-xl p-5" data-id="element-197">
                      <h4 className="text-base font-heading text-accent-blue mb-2" data-id="element-198">
                        VI. MEDIA RELEASE (OPTIONAL)
                      </h4>
                      <p className="text-sm text-gray-400 font-body mb-4" data-id="element-199">
                        I grant permission for the Coach to take photos and
                        videos during training sessions and use them for
                        promotional purposes (e.g., social media, website),
                        unless I explicitly withdraw this consent in writing.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4" data-id="element-200">
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-201">
                          <input type="radio" name="media-consent" value="agree" checked={mediaConsent === 'agree'} onChange={() => setMediaConsent('agree')} className="w-5 h-5 accent-[#00b4ff]" data-id="element-202" />
                          <span className="text-white font-body font-medium" data-id="element-203">
                            I agree
                          </span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-204">
                          <input type="radio" name="media-consent" value="disagree" checked={mediaConsent === 'disagree'} onChange={() => setMediaConsent('disagree')} className="w-5 h-5 accent-[#00b4ff]" data-id="element-205" />
                          <span className="text-white font-body font-medium" data-id="element-206">
                            I do NOT agree
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Acknowledgment */}
                    <div className="flex items-start gap-4" data-id="element-207">
                      <input type="checkbox" id="waiver-agree" required className="mt-1 w-5 h-5 rounded border-dark-border bg-dark-primary text-accent-blue focus:ring-accent-blue focus:ring-offset-0 cursor-pointer accent-[#00b4ff]" data-id="element-208" />
                      <label htmlFor="waiver-agree" className="text-sm text-gray-300 font-body cursor-pointer" data-id="element-209">
                        I HAVE READ THIS AGREEMENT, FULLY UNDERSTAND ITS TERMS,
                        AND VOLUNTARILY SIGN IT. (Parent/Guardian signature
                        required if under 18.)
                      </label>
                    </div>

                    <button type="submit" className="w-full bg-accent-blue text-black font-heading tracking-wider text-xl py-4 rounded-lg hover:bg-accent-blue-hover transition-colors" data-id="element-210">
                      SIGN AGREEMENT
                    </button>
                  </form>
                </>}
            </div>
          </div>
        </motion.div>

        {/* Location Footer */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-col items-center justify-center py-10 border-t border-dark-border" data-id="element-211">
          <div className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors" data-id="element-212">
            <MapPin className="w-6 h-6 text-accent-blue" data-id="element-213" />
            <a href="https://maps.google.com/?q=ul.+Koński+Jar+6,+02-785+Warszawa" target="_blank" rel="noopener noreferrer" className="font-body text-lg" data-id="element-214">
              Location: ul. Koński Jar 6, 02-785 Warszawa
            </a>
          </div>
        </motion.div>
      </div>
    </main>;
}